import { useEffect, useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Plus, X } from "lucide-react";
import { FaFacebook } from "react-icons/fa";
import { toast } from "sonner";
import { SortableArea } from "@/components/admin/Sortable";
import { UploadBtn } from "@/components/admin/SermonForm";
import { api, fileUrl } from "@/lib/api";
import { embedUrl, youtubeThumb } from "@/lib/media";

const input = "w-full rounded-lg border border-[#14213D]/15 bg-white px-2.5 py-2 font-ui text-xs text-[#14213D] outline-none focus:border-[#C8862F]";

// 4, 6 or 8 across on desktop, shared by the photo and video galleries.
export const ColsPicker = ({ value, onPick, t, noun }) => {
  const cols = [4, 6, 8].includes(Number(value)) ? Number(value) : 8;
  return (
    <div>
      <p className="mb-1 font-ui text-[10px] uppercase tracking-widest text-[#5F6875]">{noun} across on desktop</p>
      <div className="grid grid-cols-3 gap-1.5" role="radiogroup" aria-label="Columns on desktop">
        {[4, 6, 8].map((n) => (
          <button type="button" key={n} role="radio" aria-checked={cols === n} data-testid={`${t}-cols-${n}`} onClick={() => cols !== n && onPick(n)}
            className={`rounded-lg border py-2 font-ui text-xs font-semibold ${cols === n ? "border-[#C8862F] bg-[#C8862F]/15 text-[#14213D]" : "border-[#14213D]/15 bg-white text-[#5F6875]"}`}>{n} columns</button>
        ))}
      </div>
      <p className="mt-1 font-ui text-[11px] text-[#5F6875]">Phones always show 2 across. Tablets show 3 or 4.</p>
    </div>
  );
};

// Text box that saves when you click away.
export const Inline = ({ value = "", placeholder, onSave, testid, list }) => {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  return <input data-testid={testid} list={list} value={v} placeholder={placeholder} onChange={(e) => setV(e.target.value)} onBlur={() => v !== value && onSave(v)} className={input} />;
};

const Item = ({ v, i, t, onEdit, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: v.id });
  const thumb = v.thumb ? fileUrl(v.thumb) : youtubeThumb(v.url);
  return (
    <li ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }} data-testid={`${t}-video-${i}`} className={`flex gap-2 rounded-lg border bg-white p-2 ${isDragging ? "z-10 border-[#C8862F] shadow-lg" : "border-[#14213D]/10"}`}>
      <button type="button" data-testid={`${t}-video-drag-${i}`} {...attributes} {...listeners} aria-label="Drag to reorder" className="cursor-grab touch-none self-center rounded p-0.5 text-[#5F6875] active:cursor-grabbing"><GripVertical className="h-4 w-4" /></button>
      {thumb ? <img src={thumb} alt="" className="h-12 w-20 shrink-0 rounded object-cover" /> : <span className="grid h-12 w-20 shrink-0 place-items-center rounded" style={{ backgroundColor: "#1877F2" }}><FaFacebook className="h-5 w-5" style={{ color: "#FFFFFF" }} /></span>}
      <div className="grid min-w-0 flex-1 gap-1">
        <Inline testid={`${t}-video-title-${i}`} value={v.title} placeholder="Title" onSave={(x) => onEdit({ ...v, title: x })} />
        <Inline testid={`${t}-video-pastor-${i}`} list="ve-pastor-names" value={v.pastor} placeholder="Pastor name" onSave={(x) => onEdit({ ...v, pastor: x })} />
        <div className="flex flex-wrap items-center gap-2">
          <UploadBtn accept="image/*" testid={`${t}-video-thumb-${i}`} label={v.thumb ? "Change thumbnail" : "Upload thumbnail"} onDone={(u) => onEdit({ ...v, thumb: u })} />
          {v.thumb && <button type="button" data-testid={`${t}-video-thumb-clear-${i}`} onClick={() => onEdit({ ...v, thumb: "" })} className="font-ui text-[11px] text-[#5F6875] underline">Remove thumbnail</button>}
        </div>
      </div>
      <button type="button" data-testid={`${t}-video-remove-${i}`} onClick={onRemove} aria-label="Remove video" className="self-start rounded-full p-1 text-[#B4442A] hover:bg-[#B4442A]/10"><X className="h-3.5 w-3.5" /></button>
    </li>
  );
};

// Video gallery: paste a YouTube/Facebook link with a title and pastor, then drag to reorder.
export const VideosFields = ({ b, onChange }) => {
  const [f, setF] = useState({ url: "", title: "", pastor: "" });
  const [pastors, setPastors] = useState([]);
  const latest = useRef(b);
  latest.current = b;
  useEffect(() => { api.get("/pastors").then((r) => setPastors(Array.isArray(r.data) ? r.data.map((p) => p.name) : [])).catch(() => setPastors([])); }, []);
  const items = Array.isArray(b.items) ? b.items : [];
  const save = (list, extra = {}) => onChange({ ...latest.current, items: list, ...extra });
  const add = () => {
    const url = f.url.trim();
    if (!embedUrl(url)) return toast.error("Please paste a YouTube or Facebook video link.");
    save([...items, { id: `v-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`, url, title: f.title.trim(), pastor: f.pastor.trim() }]);
    setF({ url: "", title: "", pastor: "" });
    return toast.success("Video added to the gallery.");
  };
  const t = `ve-block-${b.id}`;
  return (
    <div className="grid gap-3">
      <ColsPicker value={b.cols} onPick={(n) => save(items, { cols: n })} t={t} noun="Videos" />
      <datalist id="ve-pastor-names">{pastors.map((n) => <option key={n} value={n} />)}</datalist>
      <div className="grid gap-1.5 rounded-xl border border-[#2563EB]/30 bg-[#2563EB]/[0.04] p-3">
        <p className="font-ui text-xs font-semibold text-[#14213D]">Add a video</p>
        <input data-testid={`${t}-new-url`} value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="Paste a YouTube or Facebook link" className={input} />
        <input data-testid={`${t}-new-title`} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" className={input} />
        <input data-testid={`${t}-new-pastor`} list="ve-pastor-names" value={f.pastor} onChange={(e) => setF({ ...f, pastor: e.target.value })} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Pastor name" className={input} />
        <button type="button" data-testid={`${t}-new-add`} onClick={add} className="mt-1 flex items-center justify-center gap-1.5 rounded-lg py-2 font-ui text-xs font-semibold" style={{ backgroundColor: "#2563EB", color: "#FFFFFF" }}><Plus className="h-3.5 w-3.5" /> Add to gallery</button>
      </div>
      {items.length > 0 && <p className="font-ui text-[11px] text-[#5F6875]">{items.length} video(s). Drag the handles to change the order.</p>}
      <SortableArea ids={items.map((v) => v.id)} onReorder={(ids) => save(ids.map((id) => items.find((v) => v.id === id)))}>
        <ul className="grid gap-2">
          {items.map((v, i) => (
            <Item key={v.id} v={v} i={i} t={t} onEdit={(nv) => save(items.map((x) => (x.id === v.id ? nv : x)))} onRemove={() => save(items.filter((x) => x.id !== v.id))} />
          ))}
        </ul>
      </SortableArea>
    </div>
  );
};
